import { useField } from "formik";
import { useEffect, useState } from "react";

import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Box, TextField, Typography } from "@mui/material";

import CompanyDetailsDrawer from "@components/CompanyDetailsDrawer/CompanyDetailsDrawer";
import CompanyDisplay from "@components/CompanyDetailsDrawer/CompanyDisplay";
import RichTextEditor from "@components/Forms/FormInputs/RichTextEditor";

import { PersonalizedEmailForContactType } from "@interfaces/campaign.type";

import { useCompanyQuery } from "@queries/company.query";

import theme from "@theme/index";

import EmailLayoutStyles from "../EmailLayoutStyles";

interface IPersonalizeCampaignDetailsProperties {
  emailIndex: number;
}

export default function EditPersonalizedEmail({ emailIndex }: IPersonalizeCampaignDetailsProperties) {
  const [focusedId, setFocusedId] = useState<string>();
  const { data: focusedCompany, fetchCompany, status: companyLoadStatus } = useCompanyQuery();

  const [subjectField] = useField<PersonalizedEmailForContactType["emailSubject"]>(
    `personalizedEmailsForContacts[${emailIndex}].emailSubject`,
  );
  const [emailField] = useField<PersonalizedEmailForContactType>(`personalizedEmailsForContacts[${emailIndex}]`);

  //TODO: Personalize.
  //
  // const { setFieldValue } = useFormikContext();
  // useEffect(() => {
  //   const currentEmail = emailField.value;
  //
  //   const isPersonalized =
  //     currentEmail.emailSubject !== currentEmail.originalSubject ||
  //     currentEmail.emailBody !== currentEmail.originalBody;
  //
  //   void setFieldValue(`personalizedEmailsForContacts[${emailIndex}].personalized`, isPersonalized);
  // }, [emailField.value, emailIndex, setFieldValue]);

  useEffect(() => {
    if (focusedId) {
      fetchCompany(focusedId);
    }
  }, [fetchCompany, focusedId]);

  return (
    <>
      <CompanyDetailsDrawer
        close={() => setFocusedId(undefined)}
        isOpen={!!focusedId && companyLoadStatus === "success"}
      >
        {focusedCompany && <CompanyDisplay close={() => setFocusedId(undefined)} company={focusedCompany} />}
      </CompanyDetailsDrawer>

      <Box sx={{ ...EmailLayoutStyles.contentRoot }}>
        <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 3 }}>
          <Box sx={{ ...EmailLayoutStyles.labelStyles }}>
            <Typography variant="body1">
              <strong>To:</strong>
            </Typography>
            {emailField.value.linkedinUrl && (
              <LinkedInIcon
                color="info"
                onClick={() => {
                  if (emailField.value.linkedinUrl) {
                    window.open(emailField.value.linkedinUrl, "_blank");
                  }
                }}
                sx={{ cursor: "pointer", height: 24, width: 24 }}
              />
            )}
            <Typography variant="body1">{emailField.value.name}</Typography>
            <Typography variant="body2">{`<${emailField.value.email}>`}</Typography>
          </Box>
          <Box sx={{ ...EmailLayoutStyles.labelStyles }}>
            <Typography variant="body1">
              <strong>Company:</strong>
            </Typography>
            <Typography
              onClick={() => {
                if (emailField.value.company?.companyId) {
                  setFocusedId(emailField.value.company.companyId);
                }
              }}
              sx={{ "&:hover": { textDecoration: "underline" }, color: theme.palette.info.main, cursor: "pointer" }}
              variant="body1"
            >
              {emailField.value.company?.name || ""}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ pb: 5 }}>
          <TextField
            color="secondary"
            id={`personalizedEmailsForContacts[${emailIndex}].emailSubject`}
            label="Subject"
            name={subjectField.name}
            onBlur={subjectField.onBlur}
            onChange={subjectField.onChange}
            size="small"
            sx={{ maxWidth: theme.breakpoints.values.sm, mb: 2, width: "100%" }}
            value={subjectField.value}
            variant="outlined"
          />
          <RichTextEditor name={`personalizedEmailsForContacts[${emailIndex}].emailBody`} />
        </Box>
      </Box>
    </>
  );
}
